import React, { useCallback, useEffect, useMemo, useState } from "react";
import Backdrop from "@mui/material/Backdrop";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import FileCopyIcon from "@mui/icons-material/FileCopyOutlined";
import Typography from "@mui/material/Typography";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FeedIcon from "@mui/icons-material/Feed";
import ShopTwoIcon from "@mui/icons-material/ShopTwo";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import copy from "copy-to-clipboard";
import toastr from "toastr";
import { axiosInstance } from "../Api/setAuthorizationToken";
import moment from "moment";
import FingerprintRoundedIcon from "@mui/icons-material/FingerprintRounded";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import AppRegistrationRoundedIcon from "@mui/icons-material/AppRegistrationRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";

const PAYMENT_DETAILS = {
  icclRbi: `*For Payments Above INR 2 lacs*
Beneficiary Account No: 8715962
Beneficiary Name: BSE Clearing Limited
Beneficiary Bank: Reserve Bank of India
Branch: Fort, Mumbai
IFSC Code: ICLL0000001
Beneficiary Account Type: Current Account
Mode of Payment accepted: *Only RTGS*
`,
  icclHdfc: `*For Payments Below INR 2 lacs*
Beneficiary Account No: 57500001086245
Beneficiary Name: BSE Clearing Limited
Beneficiary Bank: HDFC Bank Limited
Branch: Fort, Mumbai
IFSC Code: HDFC0000060
Beneficiary Account Type: Current Account
Mode of Payment accepted: *RTGS, NEFT, IMPS*
`,
  nscclRbi: `*For Payments Above INR 2 lacs*
Beneficiary Account No: 8715106
Beneficiary Name: National Securities Clearing Corporation Limited
Beneficiary Bank: Reserve Bank of India
Branch: Mumbai
IFSC Code: XNSE0000001
Beneficiary Account Type: Current Account
Mode of Payment accepted: *Only RTGS*
`,
  nscclHdfc: `*For Payments Below INR 2 lacs*
Bank Account no.CBRIC1XXXXXXXXXX (where "XXXXXXXXXX" is the PAN of Buyer)
For example: If PAN number is ABCDE1234F then beneficiary Account number shall be "CBRIC1ABCDE1234F".
Beneficiary Name : NSE Clearing Limited
Beneficiary Bank: HDFC Bank Limited
Branch: Fort, Mumbai
IFSC Code : HDFC0000060
Beneficiary Account Type: Current Account
Mode of Payment accepted: *RTGS, NEFT, IMPS*
`,
  settlementInfoIcclNsdl: `Market Instruction/Market Type: ICDM (T+0), ICDM (T+1) and ICDM (T+2)
CM Name: ICCL
CM BP ID: IN619994
Settlement Number:
Client ID: 10000010
# CM BP ID is sufficient while filling payout Instruction Slip
`,
  settlementInfoIcclCdsl: `Market Instruction/Market Type: ICDM (T+0), ICDM (T+1) and ICDM (T+2)
CM Name: ICCL
CM ID: 999
Settlement Number:
Alternate BO ID: 1100002900000030
`,
  whyInvest: `🔑 Why Invest with SMEST Capital
1. *Bond-First DNA & Proven Track Record*
Established in 2018 with a specialized focus on debt markets.
Among India’s first SEBI-registered Online Bond Platform Providers (OBPP).

2. *Exclusive High-Yield Opportunities*
Access to curated corporate bonds, NCD IPOs, and structured deals.
Limited-time offers like Power Hour and Bid Wars for superior yields.

3. *Trust, Transparency & Compliance*
SEBI-regulated platform with zero hidden charges.
Full disclosure of yields, risks, and ratings for informed decisions.

4. *Hassle-Free Investing & Easy Exits*
Smooth buy/sell experience via secondary markets.
Online Form 15G/15H submission to avoid excess TDS on interest income.

5. *SMEST Inner Circle*
Invite-only premium club for privileged investors.
Priority access to new issues, exclusive allocations, and VIP support.

6. *Safety & Research-Backed Selection*
Opportunities ranging from AAA-rated corporates to state-guaranteed bonds.
In-house research team with 0% default track record since 2018.

7. *Fast & Seamless Transactions*
Same-day T+0 or T+1 settlements.
Razorpay integration for frictionless payments.

8. *Personalized Support and after-sales*
Dedicated Relationship Managers for one-on-one guidance.
Tailored strategies aligned with client goals.

9. *Refer & Earn (Coming Soon)*
Rewards for introducing friends & family to SMEST.
Unlock special benefits as your network expands.
`,
};

const generateSpecialOfferEmailText = (
  userName: string,
  quotes: any[],
  ipos: any[]
) => {
  const festivalQuotes = quotes
    .filter(
      (q) => !!q.festival_name_for_quote && q.festival_name_for_quote !== "None"
    )
    .sort((a, b) => Number(b.sell_yield) - Number(a.sell_yield));

  const displayQuotes =
    festivalQuotes.length > 0
      ? festivalQuotes
      : [...quotes]
          .sort((a, b) => Number(b.sell_yield) - Number(a.sell_yield))
          .slice(0, 3);
  return `
Dear Investor,
  
${userName} here from SMEST Capital! I'm thrilled to welcome you to smest.in. 
As your dedicated Relationship Manager, I'm here to assist you at every step of the way.
  
Signup Link: https://smest.in/auth/signup
  
⭐Don't miss out on live IPO's⭐
  
${ipos
  .filter((item: any) => {
    const closeDate = moment(
      item.ipo_security_master.issue_close_date,
      "YYYY-MM-DD"
    );
    const cutoff = closeDate.set({
      hour: 17,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    return moment().isBefore(cutoff);
  })
  .map(
    (ipo: any) =>
      `🥳 ${ipo.ipo_security_master.issuer_of_security.issuerName.name} 
Yield Up to: ${ipo.ipo_security_master.max_interest_description}% 
Minimum Investment: INR 10,000
IPO closes on: ${moment(
        ipo.ipo_security_master.issue_close_date,
        "YYYY-MM-DD"
      ).format("DD MMM, YYYY")}
Apply now: https://smest.in/ipo-details/${ipo.ipo_security_master.issuer_of_security.issuerName.name?.replace(
        /[% ]/g,
        (m: any) => {
          const replaceChars: { [key: string]: string } = {
            "%": "",
            " ": "-",
          };
          return replaceChars[m];
        }
      )}/${ipo.ipo_security_master.uuid}`
  )
  .join("\n\n")}

🎉Check out our live Special Offers

${displayQuotes
  .map(
    (quote: any) =>
      `${quote.security_name} 
Offer Yield: ${quote.sell_yield}% 
👉 More Info: https://smest.in/bond-details/${quote.security_name.replace(
        /[% ]/g,
        (m: any) => {
          const replaceChars: { [key: string]: string } = {
            "%": "",
            " ": "-",
          };
          return replaceChars[m];
        }
      )}/${quote.isin}`
  )
  .join("\n\n")}
  
With SMEST, you have the opportunity to invest your funds and unlock greater potential!
  
  • 0️⃣Pay zero brokerage fees!
  • 👍Benefit from competitive pricing!
  • 📃Receive detailed analysis reports of the issuer!
  • 🧮Use our easy-to-use Yield and Price Calculator!
  • 📖Access our ebook on the Indian Debt Market!

For your immediate reference, please find attached our Company Profile. 
We would be delighted to schedule a call or meeting at your earliest convenience to discuss these investment opportunities in further detail and to explore how SMEST can assist you in achieving your financial objectives. Please let us know a suitable time for you.
  
Thanking you,

Best Regards
    `;
};

const generateSpecialOfferWhatsappText = (
  userName: string,
  quotes: any[],
  ipos: any[]
) => {
  // 1. Prepare the filtered & sorted festival‐offer quotes
  const festivalQuotes = quotes
    .filter(
      (q) => !!q.festival_name_for_quote && q.festival_name_for_quote !== "None"
    )
    .sort((a, b) => Number(b.sell_yield) - Number(a.sell_yield));

  // 2. If no festival offers, fall back to top 3 quotes by yield
  const displayQuotes =
    festivalQuotes.length > 0
      ? festivalQuotes
      : [...quotes]
          .sort((a, b) => Number(b.sell_yield) - Number(a.sell_yield))
          .slice(0, 3);

  return `
Hello Investor,
  
${userName} here from SMEST Capital! I'm thrilled to welcome you to smest.in. 
As your dedicated Relationship Manager, I'm here to assist you at every step of the way.
  
⭐Don't miss out on live IPO's on smest.in⭐
  
${ipos
  .filter((item: any) => {
    const closeDate = moment(
      item.ipo_security_master.issue_close_date,
      "YYYY-MM-DD"
    );
    const cutoff = closeDate.set({
      hour: 17,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    return moment().isBefore(cutoff);
  })
  .map(
    (ipo: any) =>
      `🥳 *${ipo.ipo_security_master.issuer_of_security.issuerName.name}* 
*Yield Up to*: ${ipo.ipo_security_master.max_interest_description}% 
*Minimum Investment*: INR 10,000
*IPO closes on*: ${moment(
        ipo.ipo_security_master.issue_close_date,
        "YYYY-MM-DD"
      ).format("DD MMM, YYYY")}
*Apply now*: https://smest.in/ipo-details/${ipo.ipo_security_master.issuer_of_security.issuerName.name?.replace(
        /[% ]/g,
        (m: any) => {
          const replaceChars: { [key: string]: string } = {
            "%": "",
            " ": "-",
          };
          return replaceChars[m];
        }
      )}/${ipo.ipo_security_master.uuid}`
  )
  .join("\n\n")}

🎉Check out these Special Offers we are running right now:

${displayQuotes
  .map((quote) => {
    const slug = quote.security_name.replace(/[% ]/g, (m: any) =>
      m === " " ? "-" : ""
    );
    return `*${quote.security_name}* 
*Offer Yield*: ${quote.sell_yield}% 
👉 More Info: https://smest.in/bond-details/${slug}/${quote.isin}`;
  })
  .join("\n\n")}
  
Please feel free to reach out to me for any additional information.

Best Regards,
${userName}
    `;
};

const generateStepsToBuyText = (userName: string) => {
  return `
Dear Investor,
    
1. Log on to https://smest.in and create your free account!
2. Complete your KYC in a few minutes, you will require the below documents

   a. Proof Of Identity: PAN Card
   b. Proof Of Address: Aadhar Card / Passport Copy / Driving License
   c. Bank Proof: Cancelled Cheque (from where you will be transferring the funds to the ICCL for purchasing the Bonds)
   d. Demat Proof: CML/CMR Copy
   e. Wet Signature Photograph (sign on a plain white paper and take a picture of it)
   f. Passport Size Photograph

3. Explore Bonds on the Trade Floor (https://smest.in/trade-floor)
4. Select the Bond and Swipe Right to Purchase

For any assistance, please reach out to me.

Please Note:
*All trades are settled on ICCL.*
*Please Add the below-mentioned bank as a beneficiary to your KYC'd Bank account with us.*

Beneficiary Account No: 57500001086245
Beneficiary Name: BSE Clearing Limited
Beneficiary Bank: HDFC Bank Limited
Branch: Fort, Mumbai
IFSC Code: HDFC0000060
Beneficiary Account Type: Current Account
Mode of Payment accepted: *RTGS, NEFT, IMPS*


Best Regards,
${userName}
`;
};

const generateUnavailableClientText = (userName: string) => {
  return `
Dear Sir,

This is ${userName} from SMEST Capital. I am your relationship manager at SMEST, who will be assisting you for your investments in Corporate Bonds. 

I am thrilled that you have signed up on our portal. Please let me know a good time to connect with you.

Thanking You,

Best Regards
`;
};

const generateKYCText = (
  userName: string,
  mobileNumber: string,
  emailId: string
) => {
  return `
Dear Sir,
As part of our SEBI regulated onboarding KYC process, we request you to kindly share the following documents for completing your Resident Individual KYC:
1. Photocopy of PAN Card
2. Photocopy of *Masked* Aadhaar Card
3. Photocopy of Cancelled Cheque or Bank Statement
4. Photocopy of CML / CMR
5. Soft copy of Passport-size Photograph
6. Soft copy of Wet Signature

Along with the above, please provide the following personal details:
• Marital Status
• Politically Exposed Person (PEP): Yes / No
• Maiden Name (if applicable)
• Annual Income: Less than ₹1 lac / ₹1–5 lacs / ₹5–10 lacs / ₹10–25 lacs / More than ₹25 lacs
• Gender
• Occupation
• Qualification: Below Graduate/Graduate/Post Graduate/Professional Qualification/Other
• Mother’s Name
• Differently Abled Status: Not Applicable / Different Abled
• If Differently Abled, please share: Type of Impairment, % of Impairment, UDID Number

We also request you to complete the Risk Profile Questionnaire below:
1. Age:
 • 0–17 years
 • 18–30 years
 • 31–50 years
 • 51 years & above
2. Residential Status:
 • Indian Resident
 • Non-Resident Indian
3. Investment Background:
 • Seasoned Investor
 • New to Investing
4. Financial Goal:
 • Keep Investment Safe
 • Earn Higher Returns
 • Earn Tax-Free Returns
 • Pledge for F&O
5. Bond Preference:
 • AAA rated
 • AA rated
 • A & below rated
6. Investment Horizon:
 • Up to 3 years
 • 3–5 years
 • 5 years & above

Nominee Details:
 • Name
 • Relationship
 • Date of Birth
 • Mobile Number

Kindly share the above on ${emailId}, or you can also WhatsApp the Documents on ${mobileNumber}.
Warm Regards,
${userName}
SMEST Capital
`;
};

const generateNRIKYCText = (userName: string, mobileNumber: string) => {
  return `
Dear Sir,
As part of our SEBI regulated onboarding KYC process for a *Non-Resident Individual*, we request you to kindly share the following documents for completing your KYC:
PAN Card else Form 60
Passport having Proof of Address in India
Proof of NRI Status for Indian Passport - Valid Visa or Work/Residence Permit
Proof of NRI Status for Foreign Passport - OCI Card or PIO Card or PIO Declaration
Bank Account Proof NRO or NRE (PIS or Non PIS)
Demat CML or CMR Copy
Soft copy of Passport-size Photograph
Soft copy of Wet Signature

Along with the above, please provide the following personal details:
• Marital Status
• Politically Exposed Person (PEP): Yes / No
• Maiden Name (if applicable)
• Annual Income: Less than ₹1 lac / ₹1–5 lacs / ₹5–10 lacs / ₹10–25 lacs / More than ₹25 lacs
• Gender
• Occupation
• Qualification: Below Graduate/Graduate/Post Graduate/Professional Qualification/Other
• Mother’s Name
• City of Birth
• Country of Birth
• Nationality
• Differently Abled Status: Not Applicable / Different Abled
• If Differently Abled, please share: Type of Impairment, % of Impairment, UDID Number

Any action or proceeding by SEBI, a Stock Exchange or another authority in the last 3 years? Yes or No
Investment / trading experience in (years)
Current Status: Non Resident Individual or Foreign National
Are you a Merchant Navy NRI? If Yes, please supply Mariner Declaration or CDC
Are you a Tax Resident of a Country other than India? Yes or No
If Yes, Country of Tax Residency, Account Holder Details, TIN of Functional Equivalent
Are you a US Citizen or Green Card Holder? Yes or No

We also request you to complete the Risk Profile Questionnaire below:

1. Age:
   • 0–17 years
   • 18–30 years
   • 31–50 years
   • 51 years & above
2. Residential Status:
   • Non-Resident Indian
3. Investment Background:
   • Seasoned Investor
   • New to Investing
4. Financial Goal:
   • Keep Investment Safe
   • Earn Higher Returns
   • Earn Tax-Free Returns
   • Pledge for F&O
5. Bond Preference:
   • AAA rated
   • AA rated
   • A & below rated
6. Investment Horizon:
   • Up to 3 years
   • 3–5 years
   • 5 years & above

Nominee Details:
• Name
• Relationship
• Date of Birth
• Mobile Number

Kindly share the above on care@smest.in, or you can also WhatsApp the Documents on ${mobileNumber}.

Warm Regards,
${userName}
SMEST Capital
`;
};

const SUPER_USER_DETAILS = {
  firstName: "Harsh",
  lastName: "Punjabee",
  mobileNumber: "9821151915",
  emailId: "care@smest.in",
};

export const useFetchQuotesAndIposAndAdmin = (shouldFetch: boolean) => {
  const [quotes, setQuotes] = useState<any[]>([]);
  const [ipos, setIpos] = useState<any[]>([]);
  const [adminUserRaw, setAdminUserRaw] = useState<any>(null);
  const [adminFetchDone, setAdminFetchDone] = useState(false);

  const loginUser = useMemo(() => {
    try {
      if (typeof window === "undefined") return null;
      const raw = localStorage.getItem("loginUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const userUUID = loginUser?.uuid;
  const isSuperuser = !!loginUser?.isSuperuser;
  const loginUserName = (loginUser?.name || "").trim();

  useEffect(() => {
    if (isSuperuser) {
      setAdminUserRaw(SUPER_USER_DETAILS);
      setAdminFetchDone(true);
    }
  }, [isSuperuser]);

  const fetchData = useCallback(async () => {
    if (!shouldFetch) return;

    try {
      const [quotesResp, iposResp] = await Promise.all([
        axiosInstance.post("admin/quotes/filter/v2/"),
        axiosInstance.get("/ipo/security_master/list/"),
      ]);
      if (quotesResp?.status === 200) setQuotes(quotesResp.data);
      if (iposResp?.status === 200) setIpos(iposResp.data);
    } catch (e: any) {
      toastr.error(e.message);
    }

    if (!isSuperuser && userUUID) {
      setAdminFetchDone(false);
      try {
        const usersResp = await axiosInstance.get("/users/list/");
        if (usersResp?.status === 200) {
          const matched =
            usersResp.data.find((u: any) => u.uuid === userUUID) || null;
          setAdminUserRaw(matched);
        }
      } catch (e: any) {
        toastr.error(e.message);
      } finally {
        setAdminFetchDone(true);
      }
    }
  }, [shouldFetch, isSuperuser, userUUID]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const adminUser = useMemo(() => {
    if (isSuperuser) return SUPER_USER_DETAILS;

    if (!adminFetchDone) {
      const [first, ...rest] = (loginUserName || "").split(/\s+/);
      return {
        firstName: first || "",
        lastName: rest.join(" "),
        emailId: "",
        mobileNumber: "",
      };
    }

    if (adminUserRaw) return adminUserRaw;

    const [first, ...rest] = (loginUserName || "").split(/\s+/);
    return {
      firstName: first || "",
      lastName: rest.join(" "),
      emailId: SUPER_USER_DETAILS.emailId,
      mobileNumber: SUPER_USER_DETAILS.mobileNumber,
    };
  }, [isSuperuser, adminFetchDone, adminUserRaw, loginUserName]);

  const userName = useMemo(() => {
    const n = `${adminUser.firstName || ""} ${adminUser.lastName || ""}`.trim();
    return n || loginUserName || "SMEST Admin";
  }, [adminUser, loginUserName]);

  return { quotes, ipos, adminUser, userName, adminFetchDone, isSuperuser };
};

const UtilityCopyButtons = () => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { quotes, ipos, adminUser, userName, adminFetchDone, isSuperuser } =
    useFetchQuotesAndIposAndAdmin(open);
  const { mobileNumber, emailId } = adminUser;
  const kycLoading = !isSuperuser && !adminFetchDone;

  const baseActions = useMemo(
    () => [
      {
        icon: <AppRegistrationRoundedIcon />,
        name: "WHY INVEST with SMEST",
        text: PAYMENT_DETAILS.whyInvest,
      },
      {
        icon: <AssuredWorkloadIcon />,
        name: "ICCL RBI",
        text: PAYMENT_DETAILS.icclRbi,
      },
      {
        icon: <AssuredWorkloadIcon />,
        name: "ICCL HDFC",
        text: PAYMENT_DETAILS.icclHdfc,
      },
      {
        icon: <AssuredWorkloadIcon />,
        name: "NSCCL RBI",
        text: PAYMENT_DETAILS.nscclRbi,
      },
      {
        icon: <AssuredWorkloadIcon />,
        name: "NSCCL HDFC",
        text: PAYMENT_DETAILS.nscclHdfc,
      },
      {
        icon: <MarkEmailReadIcon />,
        name: "Special Offer eMail Body",
        text: generateSpecialOfferEmailText(userName, quotes, ipos),
      },
      {
        icon: <WhatsAppIcon />,
        name: "Welcome Whatsapp to Client",
        text: generateSpecialOfferWhatsappText(userName, quotes, ipos),
      },
      {
        icon: <FeedIcon />,
        name: "Settlement Information ICCL NSDL",
        text: PAYMENT_DETAILS.settlementInfoIcclNsdl,
      },
      {
        icon: <FeedIcon />,
        name: "Settlement Information ICCL CDSL",
        text: PAYMENT_DETAILS.settlementInfoIcclCdsl,
      },
      {
        icon: <ShopTwoIcon />,
        name: "Steps to Buy",
        text: generateStepsToBuyText(userName),
      },
      {
        icon: <NoAccountsIcon />,
        name: "Unavailable client",
        text: generateUnavailableClientText(userName),
      },
    ],
    [userName, quotes, ipos]
  );

  return (
    <>
      <Backdrop
        open={open}
        sx={{
          zIndex: 99999,
        }}
      />
      <SpeedDial
        ariaLabel="Utility Tools"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 100000,
        }}
        onClose={handleClose}
        onOpen={handleOpen}
        open={open}
        icon={<SpeedDialIcon openIcon={<FileCopyIcon />} />}
      >
        {kycLoading ? (
          <SpeedDialAction
            key="KYC-loading"
            icon={
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.paper",
                  borderRadius: "50%",
                }}
              >
                <CircularProgress size={22} thickness={5} />
              </Box>
            }
            tooltipTitle={
              <Typography sx={{ whiteSpace: "nowrap" }}>
                Preparing KYC…
              </Typography>
            }
            tooltipOpen
            FabProps={{ disabled: true }}
          />
        ) : (
          <SpeedDialAction
            key="KYC"
            icon={<FingerprintRoundedIcon />}
            tooltipTitle={
              <Typography sx={{ whiteSpace: "nowrap" }}>KYC</Typography>
            }
            tooltipOpen
            onClick={() =>
              copy(generateKYCText(userName, mobileNumber, emailId))
            }
          />
        )}

        {!kycLoading && (
          <SpeedDialAction
            key="NRI-KYC"
            icon={<PublicRoundedIcon />}
            tooltipTitle={
              <Typography sx={{ whiteSpace: "nowrap" }}>NRI KYC</Typography>
            }
            tooltipOpen
            onClick={() => copy(generateNRIKYCText(userName, mobileNumber))}
          />
        )}

        {baseActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={
              <Typography sx={{ whiteSpace: "nowrap" }}>
                {action.name}
              </Typography>
            }
            tooltipOpen
            onClick={() => copy(action.text)}
          />
        ))}
      </SpeedDial>
    </>
  );
};

export default UtilityCopyButtons;
